import React, { useMemo, useCallback, useState } from 'react';
import { GameState, TransferHistoryRecord } from '../../../domain/entities';
import { View } from '../../../App';
import { soundService } from '../../../services/SoundService';
import { TeamBadge } from '../../shared/TeamBadge';
import { PotentialBadge } from '../../shared/PotentialBadge';

interface TransferHistoryProps {
    history: TransferHistoryRecord[],
    playerTeamName: string,
    pushView: (view: View) => void,
    gameState: GameState;
    filterWeek?: number;
    filterSeason?: number;
}

export const TransferHistory: React.FC<TransferHistoryProps> = ({ history, playerTeamName, pushView, gameState, filterWeek, filterSeason }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(!!filterWeek || !!filterSeason);
    const [filterPlayerName, setFilterPlayerName] = useState('');
    const [filterTeamName, setFilterTeamName] = useState('');
    const [filterSkillMin, setFilterSkillMin] = useState(0);
    const [filterSkillMax, setFilterSkillMax] = useState(120);
    const [filterValueMin, setFilterValueMin] = useState(0);
    const [filterValueMax, setFilterValueMax] = useState(999999999);
    const [filterWeekValue, setFilterWeekValue] = useState(filterWeek ?? -1);
    const [filterSeasonValue, setFilterSeasonValue] = useState(filterSeason ?? -1);

    const ITEMS_PER_PAGE = 50;

    const reversedHistory = useMemo(() => [...history].reverse(), [history]);

    // Apply filters
    const filteredHistory = useMemo(() => {
        return reversedHistory.filter(t => {
            // Defensive checks for potentially undefined fields
            const playerName = t.playerName || '';
            const fromTeam = t.fromTeam || '';
            const toTeam = t.toTeam || '';

            const playerMatch = playerName.toLowerCase().includes(filterPlayerName.toLowerCase());
            const fromTeamMatch = fromTeam.toLowerCase().includes(filterTeamName.toLowerCase());
            const toTeamMatch = toTeam.toLowerCase().includes(filterTeamName.toLowerCase());
            const teamMatch = filterTeamName === '' || fromTeamMatch || toTeamMatch;
            const skillMatch = (t.playerSkill || 0) >= filterSkillMin && (t.playerSkill || 0) <= filterSkillMax;
            const valueMatch = (t.fee || 0) >= filterValueMin && (t.fee || 0) <= filterValueMax;
            const weekMatch = filterWeekValue === -1 || t.week === filterWeekValue;
            const seasonMatch = filterSeasonValue === -1 || t.season === filterSeasonValue;

            return playerMatch && teamMatch && skillMatch && valueMatch && weekMatch && seasonMatch;
        });
    }, [reversedHistory, filterPlayerName, filterTeamName, filterSkillMin, filterSkillMax, filterValueMin, filterValueMax, filterWeekValue, filterSeasonValue]);

    // Calculate paginated list with filters applied
    const totalPages = Math.ceil(filteredHistory.length / ITEMS_PER_PAGE);
    const paginatedHistory = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredHistory.slice(startIndex, endIndex);
    }, [filteredHistory, currentPage]);

    const hasActiveFilters = filterPlayerName !== '' || filterTeamName !== '' ||
                            filterSkillMin !== 0 || filterSkillMax !== 120 ||
                            filterValueMin !== 0 || filterValueMax !== 999999999 ||
                            filterWeekValue !== -1 || filterSeasonValue !== -1;

    const resetFilters = () => {
        setFilterPlayerName('');
        setFilterTeamName('');
        setFilterSkillMin(0);
        setFilterSkillMax(120);
        setFilterValueMin(0);
        setFilterValueMax(999999999);
        setFilterWeekValue(-1);
        setFilterSeasonValue(-1);
        setCurrentPage(1);
    };

    const handlePlayerClick = (playerId: number, playerName: string) => {
        soundService.playClick();
        pushView({ type: 'playerDetail', title: `Player: ${playerName}`, playerId: playerId });
    };

    const getTeamByName = useCallback((name: string) => {
        if (name === 'Youth Academy' || name === 'Free Agent') return undefined;
        for (const league of Object.values(gameState.leagues)) {
            const team = league.teams.find(t => t.name === name);
            if (team) return team;
        }
        return undefined;
    }, [gameState.leagues]);

    const handleTeamClick = (e: React.MouseEvent, teamName: string) => {
        e.stopPropagation();
        soundService.playClick();
        if (teamName === 'Youth Academy') return;
        if (teamName === 'Free Agent') {
            // Navigate to Free Agents tab in Transfers screen
            pushView({ type: 'transfers', title: 'Transfers', activeTab: 'buy', activeBuySubTab: 'free_agents' });
            return;
        }
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName: teamName });
    };

    return (
        <div className="panel">
            <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Transfer History</span>
                <button onClick={() => setShowFilters(!showFilters)} style={{ padding: '4px 12px', fontSize: '12px' }}>
                    {showFilters ? '✕ Hide Filters' : '⚙ Filters'}
                </button>
            </div>

            {showFilters && (
                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'rgba(var(--accent-color-rgb), 0.05)', borderRadius: '4px' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9em', textTransform: 'uppercase', opacity: 0.8 }}>Filters</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85em', marginBottom: '4px', opacity: 0.7 }}>Player Name</label>
                            <input
                                type="text"
                                placeholder="Search player..."
                                value={filterPlayerName}
                                onChange={(e) => { setFilterPlayerName(e.target.value); setCurrentPage(1); }}
                                style={{ width: '100%', padding: '6px', backgroundColor: 'var(--primary-color)', color: 'var(--text-color)', border: '1px solid rgba(var(--accent-color-rgb), 0.3)', borderRadius: '3px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85em', marginBottom: '4px', opacity: 0.7 }}>Team Name</label>
                            <input
                                type="text"
                                placeholder="Search team..."
                                value={filterTeamName}
                                onChange={(e) => { setFilterTeamName(e.target.value); setCurrentPage(1); }}
                                style={{ width: '100%', padding: '6px', backgroundColor: 'var(--primary-color)', color: 'var(--text-color)', border: '1px solid rgba(var(--accent-color-rgb), 0.3)', borderRadius: '3px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85em', marginBottom: '4px', opacity: 0.7 }}>Min Skill</label>
                            <input
                                type="number"
                                min="0"
                                max="120"
                                value={filterSkillMin}
                                onChange={(e) => { setFilterSkillMin(Number(e.target.value)); setCurrentPage(1); }}
                                style={{ width: '100%', padding: '6px', backgroundColor: 'var(--primary-color)', color: 'var(--text-color)', border: '1px solid rgba(var(--accent-color-rgb), 0.3)', borderRadius: '3px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85em', marginBottom: '4px', opacity: 0.7 }}>Max Skill</label>
                            <input
                                type="number"
                                min="0"
                                max="120"
                                value={filterSkillMax}
                                onChange={(e) => { setFilterSkillMax(Number(e.target.value)); setCurrentPage(1); }}
                                style={{ width: '100%', padding: '6px', backgroundColor: 'var(--primary-color)', color: 'var(--text-color)', border: '1px solid rgba(var(--accent-color-rgb), 0.3)', borderRadius: '3px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85em', marginBottom: '4px', opacity: 0.7 }}>Min Value</label>
                            <input
                                type="number"
                                min="0"
                                value={filterValueMin}
                                onChange={(e) => { setFilterValueMin(Number(e.target.value)); setCurrentPage(1); }}
                                style={{ width: '100%', padding: '6px', backgroundColor: 'var(--primary-color)', color: 'var(--text-color)', border: '1px solid rgba(var(--accent-color-rgb), 0.3)', borderRadius: '3px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85em', marginBottom: '4px', opacity: 0.7 }}>Max Value</label>
                            <input
                                type="number"
                                min="0"
                                value={filterValueMax}
                                onChange={(e) => { setFilterValueMax(Number(e.target.value)); setCurrentPage(1); }}
                                style={{ width: '100%', padding: '6px', backgroundColor: 'var(--primary-color)', color: 'var(--text-color)', border: '1px solid rgba(var(--accent-color-rgb), 0.3)', borderRadius: '3px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85em', marginBottom: '4px', opacity: 0.7 }}>Season</label>
                            <select
                                value={filterSeasonValue}
                                onChange={(e) => { setFilterSeasonValue(Number(e.target.value)); setCurrentPage(1); }}
                                style={{ width: '100%', padding: '6px', backgroundColor: 'var(--primary-color)', color: 'var(--text-color)', border: '1px solid rgba(var(--accent-color-rgb), 0.3)', borderRadius: '3px' }}
                            >
                                <option value="-1">All Seasons</option>
                                {Array.from({ length: gameState.season + 1 }, (_, i) => i + 1).reverse().map(s => (
                                    <option key={s} value={s}>Season {s}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85em', marginBottom: '4px', opacity: 0.7 }}>Week</label>
                            <select
                                value={filterWeekValue}
                                onChange={(e) => { setFilterWeekValue(Number(e.target.value)); setCurrentPage(1); }}
                                style={{ width: '100%', padding: '6px', backgroundColor: 'var(--primary-color)', color: 'var(--text-color)', border: '1px solid rgba(var(--accent-color-rgb), 0.3)', borderRadius: '3px' }}
                            >
                                <option value="-1">All Weeks</option>
                                {Array.from({ length: gameState.config.WEEKS_PER_SEASON }, (_, i) => i + 1).reverse().map(w => (
                                    <option key={w} value={w}>Week {w}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {hasActiveFilters && (
                        <button
                            onClick={resetFilters}
                            style={{ marginTop: '12px', padding: '6px 12px', fontSize: '11px', backgroundColor: 'rgba(var(--accent-color-rgb), 0.2)', border: '1px solid rgba(var(--accent-color-rgb), 0.4)', color: 'var(--text-color)', borderRadius: '3px', cursor: 'pointer' }}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
            )}

            <div style={{ marginBottom: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                Showing {paginatedHistory.length} of {filteredHistory.length} transfers
            </div>

            <div className="table-wrapper">
                <table className="table">
                    <thead>
                        <tr><th>S/W</th><th>Player</th><th>Age</th><th>Pos</th><th>Skill</th><th>Pot</th><th>From</th><th>To</th><th style={{textAlign: 'right'}}>Fee</th></tr>
                    </thead>
                    <tbody>
                        {filteredHistory.length === 0 && (
                            <tr>
                                <td colSpan={9} style={{textAlign: 'center'}}>No transfers match your filters.</td>
                            </tr>
                        )}
                        {paginatedHistory.map((t, i) => {
                            const fromTeam = t.fromTeam || '';
                            const toTeam = t.toTeam || '';
                            const isPlayerTeamInvolved = fromTeam === playerTeamName || toTeam === playerTeamName;
                            return (
                                <tr key={i} className={isPlayerTeamInvolved ? 'highlight' : ''}>
                                    <td>S{t.season} W{t.week}</td>
                                    <td>
                                        <button className="link-style" onClick={() => handlePlayerClick(t.playerId, t.playerName)}>{t.playerName}</button>
                                    </td>
                                    <td>{t.playerAge}</td>
                                    <td>{t.playerPosition}</td>
                                    <td>{t.playerSkill}</td>
                                    <td><PotentialBadge potential={t.playerPotential} /></td>
                                    <td>
                                        <button className="link-style" onClick={(e) => handleTeamClick(e, fromTeam)}>
                                            <span className={fromTeam === playerTeamName ? 'player-team-name' : ''}>{getTeamByName(fromTeam)?.badge && <TeamBadge badge={getTeamByName(fromTeam)?.badge} color={getTeamByName(fromTeam)?.colors.primary} size="sm" />}{fromTeam}</span>
                                        </button>
                                    </td>
                                    <td>
                                        <button className="link-style" onClick={(e) => handleTeamClick(e, toTeam)}>
                                            <span className={toTeam === playerTeamName ? 'player-team-name' : ''}>{getTeamByName(toTeam)?.badge && <TeamBadge badge={getTeamByName(toTeam)?.badge} color={getTeamByName(toTeam)?.colors.primary} size="sm" />}{toTeam}</span>
                                        </button>
                                    </td>
                                    <td style={{textAlign: 'right'}}>£{(t.fee || 0).toLocaleString()}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {reversedHistory.length > ITEMS_PER_PAGE && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--spacing-sm)', padding: 'var(--spacing-lg)', backgroundColor: 'var(--primary-color)', borderTop: 'var(--border)' }}>
                    <button
                        className="button small"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                    >
                        ← Prev
                    </button>
                    <span style={{ fontSize: '0.9em', opacity: 0.8 }}>
                        Page {currentPage} of {totalPages} ({reversedHistory.length} transfers)
                    </span>
                    <button
                        className="button small"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                    >
                        Next →
                    </button>
                </div>
            )}
        </div>
    );
};
